import type {RevaSpecApiResponse} from "@reva/shared/reva-spec";
import type {RevaSpec, SpecConversionConfig} from "@reva/shared/spec-conversion";
import {convertRevaSpecResponse} from "@reva/shared/spec-conversion";

import type ServiceApi from "../api";
import {AvmApi} from "../api/api-constants";
import {SimpleApiInterface} from "../api/api-interface";
import type {ApiCredentials} from "../api/credentials";
import type {ApiExecutor} from "../api/executor";
import {generateDefaultQueryError} from "../util/errorUtil";
import {selectCredentials} from "./authSlice";
import type {RevaThunkAction} from "./revaHooks";
import {selectLoading, setLoading, setQueryError, setSpecData} from "./specSlice";

export const SPEC_CONVERSION_CONFIG: SpecConversionConfig = {
    inputSegments: [
        {key: "VALUE", parameterType: "AVM"},
        {key: "BUILDING_CATEGORY", parameterType: "AVM"},
        {key: "MODERNIZATION_DEGREE", parameterType: "AVM"},
        {key: "QUALITY_FURNISHINGS", parameterType: "AVM"},
    ],
    outputSegments: [
        {key: "BUILDING_CATEGORY", parameterType: "AVM"},
        {key: "MODERNIZATION_DEGREE", parameterType: "AVM"},
        {key: "QUALITY_FURNISHINGS", parameterType: "AVM"},
        {key: "ENERGY_VALUE", parameterType: "ENERGY_CERT"},
        {key: "VALUE", parameterType: "AVM"},
    ],
};

/**
 * API interface for the REVA spec request (GET {@link AvmApi.PATH_REVA_SPEC}).
 * The raw response is converted to the internal {@link RevaSpec} shape in executeInternal.
 */
export class RevaSpecApiInterface extends SimpleApiInterface<RevaSpec> {
    protected buildUrl(api: ServiceApi): string {
        return api.buildAvmApiUrl([AvmApi.PATH_REVA_SPEC]);
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<RevaSpec> {
        const apiResponse: RevaSpecApiResponse = await executor.fetchGET(url, credentials);
        return convertRevaSpecResponse(apiResponse, SPEC_CONVERSION_CONFIG);
    }
}

/**
 * Async thunk to load the spec(s) used in REVA.
 */
export const querySpecsAsync =
    (): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, {env, serviceApi}) => {
        const specLoading = selectLoading(getState());
        if (specLoading) {
            if (env.isDevelopmentMode) {
                console.warn("Skipped loading spec again, because spec is already loaded at the moment.");
                return;
            }
        }
        dispatch(setLoading());

        const credentials = selectCredentials(getState());
        try {
            if (env.isDevelopmentMode) {
                console.log(`Loading spec ...`);
            }
            const specData = await new RevaSpecApiInterface().execute(serviceApi, credentials!);
            if (!specData) {
                dispatch(
                    setQueryError({
                        error: "general.error.invalid",
                        errorHasContactLink: true,
                    }),
                );
                return;
            }
            dispatch(setSpecData(specData));
        } catch (err) {
            dispatch(setQueryError(generateDefaultQueryError(err as Error)));
        }
    };
